import { useCallback, useEffect, useRef, useState } from "react";
import {
  CaretDown, CaretUp, Microphone, MicrophoneSlash, Phone, PhoneDisconnect, X,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const RINGTONE_URL = `${process.env.PUBLIC_URL || ""}/phone_bell.wav`;
const DTMF_BASE = `${process.env.PUBLIC_URL || ""}/dtmf`;
const DTMF_FILE = {
  "0": "0.wav", "1": "1.wav", "2": "2.wav", "3": "3.wav", "4": "4.wav",
  "5": "5.wav", "6": "6.wav", "7": "7.wav", "8": "8.wav", "9": "9.wav",
  "*": "star.wav", "#": "pound.wav",
};
/**
 * Collapsible Webphone for agents with phone_type=web.
 * Loads SIP credentials from the logged-in user (/agent/webphone) and REGISTERs
 * over WSS so the Conference Session INVITE can be answered here.
 */
export default function AgentWebphone({
  defaultExpanded = false,
  gateMode = false,
  onRegistered,
  onStatusChange,
  className = "",
}) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!!defaultExpanded || !!gateMode);
  const [digits, setDigits] = useState("");
  // idle | connecting | registered | ringing | answering | incall | error
  const [status, setStatus] = useState("idle");
  const [statusLine, setStatusLine] = useState("");
  const [muted, setMuted] = useState(false);
  const [volume] = useState(0.85);
  const [hangupConfirmOpen, setHangupConfirmOpen] = useState(false);
  const uaRef = useRef(null);
  const regRef = useRef(null);
  const sessionRef = useRef(null);
  const answeringRef = useRef(false);
  const remoteAudioRef = useRef(null);
  const localStreamRef = useRef(null);
  const ringtoneRef = useRef(null);
  const dtmfCacheRef = useRef({});
  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  const onRegisteredRef = useRef(onRegistered);
  const onStatusChangeRef = useRef(onStatusChange);
  onRegisteredRef.current = onRegistered;
  onStatusChangeRef.current = onStatusChange;
  // Auto re-register / reconnect bookkeeping (survives renders)
  const intentionalStopRef = useRef(false);
  const ensureTimerRef = useRef(null);
  const ensureInFlightRef = useRef(false);
  const backoffMsRef = useRef(1000);
  const RegistererStateRef = useRef(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  const stopRingtone = useCallback(() => {
    const audio = ringtoneRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch { /* */ }
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone();
    try {
      let audio = ringtoneRef.current;
      if (!audio) {
        audio = new Audio(RINGTONE_URL);
        audio.loop = true;
        audio.preload = "auto";
        ringtoneRef.current = audio;
      }
      audio.volume = Math.min(1, Math.max(0.2, volumeRef.current ?? 0.85));
      audio.currentTime = 0;
      audio.play().catch(() => { /* autoplay may need a prior gesture */ });
    } catch { /* */ }
  }, [stopRingtone]);

  const startRingtoneRef = useRef(startRingtone);
  const stopRingtoneRef = useRef(stopRingtone);
  startRingtoneRef.current = startRingtone;
  stopRingtoneRef.current = stopRingtone;

  const setPhoneStatus = useCallback((next, line) => {
    setStatus(next);
    if (line != null) setStatusLine(line);
    try { onStatusChangeRef.current?.(next, line); } catch { /* */ }
  }, []);

  const clearEnsureTimer = useCallback(() => {
    if (ensureTimerRef.current) {
      clearTimeout(ensureTimerRef.current);
      ensureTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(async () => {
    intentionalStopRef.current = true;
    clearEnsureTimer();
    stopRingtone();
    try {
      if (sessionRef.current) {
        try { await sessionRef.current.bye(); } catch { /* */ }
        sessionRef.current = null;
      }
      if (regRef.current) {
        try { await regRef.current.unregister(); } catch { /* */ }
        regRef.current = null;
      }
      if (uaRef.current) {
        try { await uaRef.current.stop(); } catch { /* */ }
        uaRef.current = null;
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
    } catch { /* */ }
  }, [clearEnsureTimer, stopRingtone]);

  useEffect(() => {
    if (gateMode || defaultExpanded) setExpanded(true);
  }, [gateMode, defaultExpanded]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agent/webphone");
        if (cancelled) return;
        if (!data?.enabled) {
          setCfg(null);
          setLoading(false);
          return;
        }
        setCfg(data);
      } catch {
        if (!cancelled) setCfg(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stable callback — must NOT change when volume changes (that would re-run
  // the SIP REGISTER effect and tear down the call).
  const attachRemoteAudio = useCallback((session) => {
    try {
      const pc = session.sessionDescriptionHandler?.peerConnection;
      if (!pc || !remoteAudioRef.current) return;
      const remoteStream = new MediaStream();
      pc.getReceivers().forEach((r) => {
        if (r.track) remoteStream.addTrack(r.track);
      });
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.volume = volumeRef.current;
      remoteAudioRef.current.play().catch(() => {});
    } catch { /* */ }
  }, []);

  useEffect(() => {
    if (!cfg?.enabled) return undefined;
    let cancelled = false;
    intentionalStopRef.current = false;
    backoffMsRef.current = 1000;

    const scheduleEnsureRegistered = (reason, delayMs) => {
      if (cancelled || intentionalStopRef.current) return;
      clearEnsureTimer();
      const wait = delayMs != null ? delayMs : backoffMsRef.current;
      ensureTimerRef.current = setTimeout(() => {
        ensureTimerRef.current = null;
        ensureRegistered(reason);
      }, Math.max(250, wait));
    };

    const markRegistered = () => {
      backoffMsRef.current = 1000;
      const user = cfgRef.current?.sip_username || cfg.sip_username;
      // Don't clobber an active call UI with the idle registered line
      const st = statusRef.current;
      if (st !== "ringing" && st !== "answering" && st !== "incall") {
        setPhoneStatus("registered", `Registered · ${user}`);
      }
      try { onRegisteredRef.current?.(cfgRef.current || cfg); } catch { /* */ }
    };

    const wireRegisterer = (registerer, RegistererState) => {
      registerer.stateChange.addListener((st) => {
        if (st === RegistererState.Registered) markRegistered();
        if (
          st === RegistererState.Unregistered
          && !intentionalStopRef.current
          && !cancelled
        ) {
          scheduleEnsureRegistered("unregister", backoffMsRef.current);
          backoffMsRef.current = Math.min(30000, backoffMsRef.current * 2);
        }
      });
    };

    const bootstrapSip = async (statusLabel = "Registering…") => {
      if (cancelled || intentionalStopRef.current) return false;
      setPhoneStatus("connecting", statusLabel);
      // Mic permission early so Answer works on the ringing screen
      if (!localStreamRef.current) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          localStreamRef.current = stream;
        } catch {
          /* Answer may still prompt */
        }
      }

      const sipJs = await import("sip.js");
      const { UserAgent, Registerer, RegistererState, SessionState } = sipJs;
      RegistererStateRef.current = RegistererState;
      const liveCfg = cfgRef.current || cfg;
      const uri = UserAgent.makeURI(`sip:${liveCfg.sip_username}@${liveCfg.sip_realm}`);
      if (!uri) throw new Error("Invalid SIP URI");

      // Drop any half-dead UA before creating a new one
      if (uaRef.current) {
        try { await uaRef.current.stop(); } catch { /* */ }
        uaRef.current = null;
        regRef.current = null;
      }

      const ua = new UserAgent({
        uri,
        transportOptions: { server: liveCfg.wss_url },
        authorizationUsername: liveCfg.sip_username,
        authorizationPassword: liveCfg.sip_password,
        displayName: liveCfg.display_name || liveCfg.sip_username,
        sessionDescriptionHandlerFactoryOptions: {
          constraints: { audio: true, video: false },
        },
        // Built-in WSS reconnect when the socket drops with an error
        reconnectionAttempts: 32,
        reconnectionDelay: 2,
        logBuiltinEnabled: false,
      });

      ua.delegate = {
        onConnect: () => {
          if (cancelled || intentionalStopRef.current) return;
          scheduleEnsureRegistered("transport-connect", 300);
        },
        onDisconnect: (error) => {
          if (cancelled || intentionalStopRef.current) return;
          const st = statusRef.current;
          const busyCall = st === "ringing" || st === "answering" || st === "incall";
          if (!busyCall) {
            setPhoneStatus(
              "connecting",
              error
                ? "Connection lost — reconnecting…"
                : "Webphone disconnected — reconnecting…",
            );
          }
          // sip.js auto-reconnects when error is set; also schedule our REGISTER restore
          scheduleEnsureRegistered("transport-disconnect", error ? 1500 : 800);
        },
        onInvite: (invitation) => {
          sessionRef.current = invitation;
          answeringRef.current = false;
          const remote = invitation.remoteIdentity?.uri?.user
            || invitation.remoteIdentity?.displayName
            || "conference";
          setExpanded(true);
          setPhoneStatus("ringing", `Incoming - ${remote}`);
          startRingtoneRef.current();
          invitation.stateChange.addListener((s) => {
            if (s === SessionState.Establishing) {
              stopRingtoneRef.current();
              answeringRef.current = true;
              if (statusRef.current !== "incall") {
                setPhoneStatus("answering", "Connecting…");
              }
            }
            if (s === SessionState.Established) {
              stopRingtoneRef.current();
              answeringRef.current = false;
              attachRemoteAudio(invitation);
              setPhoneStatus("incall", `Incall - ${remote}`);
            }
            if (s === SessionState.Terminated) {
              stopRingtoneRef.current();
              answeringRef.current = false;
              sessionRef.current = null;
              const reg = regRef.current;
              if (reg && RegistererStateRef.current
                && reg.state === RegistererStateRef.current.Registered) {
                setPhoneStatus("registered", `Registered · ${liveCfg.sip_username}`);
              } else {
                scheduleEnsureRegistered("after-call", 500);
              }
            }
          });
        },
      };

      await ua.start();
      if (cancelled) {
        await ua.stop();
        return false;
      }
      uaRef.current = ua;
      const registerer = new Registerer(ua);
      regRef.current = registerer;
      wireRegisterer(registerer, RegistererState);
      await registerer.register();
      if (!cancelled && registerer.state !== RegistererState.Registered) {
        await new Promise((resolve) => {
          const t = setTimeout(resolve, 10000);
          registerer.stateChange.addListener((st) => {
            if (st === RegistererState.Registered || st === RegistererState.Terminated) {
              clearTimeout(t);
              resolve();
            }
          });
        });
      }
      if (cancelled) return false;
      if (registerer.state === RegistererState.Registered) {
        markRegistered();
        return true;
      }
      return false;
    };

    const ensureRegistered = async (reason = "retry") => {
      if (cancelled || intentionalStopRef.current) return;
      if (ensureInFlightRef.current) {
        scheduleEnsureRegistered(reason, 1500);
        return;
      }

      ensureInFlightRef.current = true;
      try {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          setPhoneStatus("connecting", "Network offline — will re-register when online…");
          return;
        }

        // UA never started or was destroyed — full bootstrap
        if (!uaRef.current || !RegistererStateRef.current) {
          const ok = await bootstrapSip(
            reason === "online" ? "Network restored — re-registering…" : "Reconnecting…",
          );
          if (!ok && !cancelled && !intentionalStopRef.current) {
            backoffMsRef.current = Math.min(30000, backoffMsRef.current * 2);
            scheduleEnsureRegistered(reason, backoffMsRef.current);
          }
          return;
        }

        const ua = uaRef.current;
        const RegistererState = RegistererStateRef.current;
        const busyCall = statusRef.current === "ringing" || statusRef.current === "answering" || statusRef.current === "incall";
        if (!busyCall) {
          setPhoneStatus(
            "connecting",
            reason === "online"
              ? "Network restored — re-registering…"
              : reason === "unregister"
                ? "Re-registering…"
                : "Reconnecting…",
          );
        }

        if (typeof ua.isConnected === "function" && !ua.isConnected()) {
          try {
            await ua.reconnect();
          } catch {
            // Mid-call: keep retrying reconnect only — rebuilding UA would drop audio.
            if (busyCall) {
              backoffMsRef.current = Math.min(30000, backoffMsRef.current * 2);
              scheduleEnsureRegistered(reason, backoffMsRef.current);
              return;
            }
            // Transport too dead — rebuild UA + REGISTER
            const ok = await bootstrapSip("Reconnecting…");
            if (!ok && !cancelled && !intentionalStopRef.current) {
              backoffMsRef.current = Math.min(30000, backoffMsRef.current * 2);
              scheduleEnsureRegistered(reason, backoffMsRef.current);
            }
            return;
          }
        }

        let registerer = regRef.current;
        if (!registerer || registerer.state === RegistererState.Terminated) {
          const sipJs = await import("sip.js");
          registerer = new sipJs.Registerer(ua);
          regRef.current = registerer;
          wireRegisterer(registerer, RegistererState);
        }

        if (registerer.state === RegistererState.Registered) {
          markRegistered();
          return;
        }

        try {
          await registerer.register();
        } catch (e) {
          // Overlapping REGISTER is fine — wait for state listener / next retry
          if (!/already in progress/i.test(String(e?.message || e))) throw e;
        }

        if (registerer.state !== RegistererState.Registered) {
          await new Promise((resolve) => {
            const t = setTimeout(resolve, 8000);
            registerer.stateChange.addListener((st) => {
              if (
                st === RegistererState.Registered
                || st === RegistererState.Unregistered
                || st === RegistererState.Terminated
              ) {
                clearTimeout(t);
                resolve();
              }
            });
          });
        }

        if (cancelled || intentionalStopRef.current) return;
        if (registerer.state === RegistererState.Registered) {
          markRegistered();
        } else {
          backoffMsRef.current = Math.min(30000, backoffMsRef.current * 2);
          scheduleEnsureRegistered(reason, backoffMsRef.current);
          if (!busyCall) {
            setPhoneStatus(
              "connecting",
              `Registration lost — retrying in ${Math.round(backoffMsRef.current / 1000)}s…`,
            );
          }
        }
      } catch (e) {
        if (cancelled || intentionalStopRef.current) return;
        backoffMsRef.current = Math.min(30000, backoffMsRef.current * 2);
        scheduleEnsureRegistered(reason, backoffMsRef.current);
        const busyCall = statusRef.current === "ringing" || statusRef.current === "answering" || statusRef.current === "incall";
        if (!busyCall) {
          const raw = String(e?.message || e || "reconnect failed");
          setPhoneStatus(
            "connecting",
            /1015|certificate|TLS|SSL/i.test(raw)
              ? "Secure WebSocket issue — retrying…"
              : `Connection issue — retrying in ${Math.round(backoffMsRef.current / 1000)}s…`,
          );
        }
      } finally {
        ensureInFlightRef.current = false;
      }
    };

    (async () => {
      try {
        const ok = await bootstrapSip("Registering…");
        if (!ok && !cancelled && !intentionalStopRef.current) {
          setPhoneStatus("connecting", "Webphone could not register — retrying…");
          scheduleEnsureRegistered("initial-fail", 2000);
        }
      } catch (e) {
        if (!cancelled) {
          const raw = String(e?.message || e || "Webphone failed");
          const msg = /1015|certificate|TLS|SSL/i.test(raw)
            ? "Secure WebSocket blocked (TLS). Retrying…"
            : raw;
          setPhoneStatus("connecting", msg);
          scheduleEnsureRegistered("initial-error", 3000);
        }
      }
    })();

    const onOnline = () => {
      backoffMsRef.current = 1000;
      scheduleEnsureRegistered("online", 400);
    };
    const onOffline = () => {
      if (cancelled || intentionalStopRef.current) return;
      const busyCall = statusRef.current === "ringing" || statusRef.current === "answering" || statusRef.current === "incall";
      if (!busyCall) {
        setPhoneStatus("connecting", "Network offline — will re-register when online…");
      }
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const reg = regRef.current;
      const RS = RegistererStateRef.current;
      const ua = uaRef.current;
      if (!ua || !RS) return;
      const needs =
        (typeof ua.isConnected === "function" && !ua.isConnected())
        || !reg
        || reg.state !== RS.Registered;
      if (needs) scheduleEnsureRegistered("visibility", 500);
    };
    // Watchdog: catch silent unregister / dead sockets while the tab stays open
    const watchdog = setInterval(() => {
      if (cancelled || intentionalStopRef.current || ensureInFlightRef.current) return;
      const ua = uaRef.current;
      const reg = regRef.current;
      const RS = RegistererStateRef.current;
      if (!ua || !RS) return;
      const deadSocket = typeof ua.isConnected === "function" && !ua.isConnected();
      const unreg = !reg || reg.state !== RS.Registered;
      if (deadSocket || unreg) scheduleEnsureRegistered("watchdog", 0);
    }, 15000);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(watchdog);
      clearEnsureTimer();
      teardown();
    };
  }, [cfg, teardown, setPhoneStatus, attachRemoteAudio, clearEnsureTimer]);

  // Volume only adjusts audio elements — never remount SIP.
  useEffect(() => {
    if (remoteAudioRef.current) remoteAudioRef.current.volume = volume;
    if (ringtoneRef.current) {
      ringtoneRef.current.volume = Math.min(1, Math.max(0.2, volume));
    }
  }, [volume]);

  const playDtmfTone = useCallback((k) => {
    const file = DTMF_FILE[k];
    if (!file) return;
    try {
      let audio = dtmfCacheRef.current[k];
      if (!audio) {
        audio = new Audio(`${DTMF_BASE}/${file}`);
        audio.preload = "auto";
        dtmfCacheRef.current[k] = audio;
      }
      audio.volume = Math.min(1, Math.max(0.25, volumeRef.current ?? 0.85));
      audio.currentTime = 0;
      audio.play().catch(() => { /* ignore autoplay blocks */ });
    } catch { /* */ }
  }, []);

  const pressKey = (k) => {
    setDigits((d) => (d + k).slice(0, 32));
    playDtmfTone(k);
    const sess = sessionRef.current;
    if (sess?.sessionDescriptionHandler?.sendDtmf) {
      try { sess.sessionDescriptionHandler.sendDtmf(k); } catch { /* */ }
    }
  };

  const answer = async () => {
    const sess = sessionRef.current;
    if (!sess) return;
    // Already accepting / connected — ignore double-clicks (sip.js throws
    // "Invalid session state Establishing" on a second accept()).
    if (answeringRef.current || status === "answering" || status === "incall") return;
    if (status !== "ringing") return;
    answeringRef.current = true;
    stopRingtone();
    const remote = sess.remoteIdentity?.uri?.user
      || sess.remoteIdentity?.displayName
      || "0000000000";
    // Flip UI immediately so Answer disables and the agent gate can advance
    // while ICE/DTLS finishes (often several seconds).
    setPhoneStatus("answering", "Connecting…");
    try {
      const { SessionState } = await import("sip.js");
      if (sess.state === SessionState.Established) {
        attachRemoteAudio(sess);
        answeringRef.current = false;
        setPhoneStatus("incall", `Incall - ${remote}`);
        return;
      }
      if (sess.state === SessionState.Establishing) {
        // First accept() still in flight — Established listener finishes UI
        return;
      }
      await sess.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      attachRemoteAudio(sess);
      answeringRef.current = false;
      setPhoneStatus("incall", `Incall - ${remote}`);
    } catch (e) {
      const msg = String(e?.message || e || "Answer failed");
      // Harmless race: second click (or late accept) while sip.js is Establishing
      if (/establishing/i.test(msg) || /invalid session state/i.test(msg)) {
        if (statusRef.current !== "incall") {
          setPhoneStatus("answering", "Connecting…");
        }
        return;
      }
      answeringRef.current = false;
      setPhoneStatus("error", msg);
    }
  };

  const hangup = async () => {
    setHangupConfirmOpen(false);
    const sess = sessionRef.current;
    if (!sess) return;
    stopRingtone();
    try {
      const { SessionState } = await import("sip.js");
      if (sess.state === SessionState.Established) await sess.bye();
      else if (typeof sess.reject === "function") await sess.reject();
      else if (typeof sess.cancel === "function") await sess.cancel();
    } catch { /* */ }
    sessionRef.current = null;
    const RS = RegistererStateRef.current;
    const reg = regRef.current;
    if (reg && RS && reg.state === RS.Registered) {
      setPhoneStatus("registered", cfg ? `Registered · ${cfg.sip_username}` : "");
    } else {
      setPhoneStatus("connecting", "Re-registering…");
      // Watchdog / online handlers will restore REGISTER; nudge immediately
      try {
        window.dispatchEvent(new Event("online"));
      } catch { /* */ }
    }
  };

  const requestHangup = () => {
    if (!sessionRef.current) return;
    // Declining a ring does not tear down an active agent session — hang up immediately.
    if (status === "ringing") {
      hangup();
      return;
    }
    setHangupConfirmOpen(true);
  };

  const dial = async () => {
    if (!uaRef.current || !cfg || !digits.trim()) return;
    try {
      const { Inviter, SessionState, UserAgent } = await import("sip.js");
      const targetUri = UserAgent.makeURI(`sip:${digits.trim()}@${cfg.sip_realm}`);
      if (!targetUri) return;
      const inviter = new Inviter(uaRef.current, targetUri);
      sessionRef.current = inviter;
      setPhoneStatus("incall", `Calling - ${digits.trim()}`);
      inviter.stateChange.addListener((s) => {
        if (s === SessionState.Established) attachRemoteAudio(inviter);
        if (s === SessionState.Terminated) {
          sessionRef.current = null;
          setPhoneStatus("registered", `Registered · ${cfg.sip_username}`);
        }
      });
      await inviter.invite();
    } catch (e) {
      setPhoneStatus("error", e?.message || "Dial failed");
    }
  };

  const toggleMute = () => {
    const sess = sessionRef.current;
    const pc = sess?.sessionDescriptionHandler?.peerConnection;
    if (!pc) {
      setMuted((m) => !m);
      return;
    }
    const next = !muted;
    pc.getSenders().forEach((s) => {
      if (s.track && s.track.kind === "audio") s.track.enabled = !next;
    });
    setMuted(next);
  };

  if (loading) {
    if (!gateMode) return null;
    return (
      <div className={`shrink-0 border border-border bg-white ${className}`} data-testid="agent-webphone-loading">
        <div className="h-9 bg-[#1e3a5f] text-white text-sm font-medium grid place-items-center">Webphone</div>
        <p className="text-xs text-muted-foreground p-3">Loading credentials…</p>
      </div>
    );
  }

  if (!cfg?.enabled) return null;

  const inCall = status === "incall";
  const isRinging = status === "ringing";
  const isAnswering = status === "answering";
  const answerLocked = isAnswering || inCall;

  const toggleExpand = () => setExpanded((v) => !v);

  return (
    <div
      className={`shrink-0 border-t border-border bg-white ${className}`}
      data-testid="agent-webphone"
    >
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
      <Dialog open={hangupConfirmOpen} onOpenChange={setHangupConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="agent-webphone-hangup-confirm">
          <DialogHeader>
            <DialogTitle>Warning: Active Agent Session</DialogTitle>
            <DialogDescription className="text-sm text-foreground/90 space-y-2 pt-1">
              <span className="block">
                You are currently in an active session with the agent interface. If you hang up this call, your session with the agent interface will be disconnected.
              </span>
              <span className="block font-medium">Are you sure you want to continue?</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setHangupConfirmOpen(false)}
              data-testid="agent-webphone-hangup-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={hangup}
              data-testid="agent-webphone-hangup-confirm-btn"
            >
              Hang up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Blue bar — click toggles minimize / maximize */}
      <button
        type="button"
        className={`w-full h-9 px-3 flex items-center justify-between text-white text-sm font-medium tracking-wide ${
          isRinging && !expanded ? "bg-emerald-600 animate-pulse" : "bg-[#1e3a5f]"
        }`}
        onClick={toggleExpand}
        aria-expanded={expanded}
        aria-label={expanded ? "Minimize Webphone" : "Maximize Webphone"}
        data-testid="agent-webphone-bar"
      >
        <span>{isRinging && !expanded ? "Webphone — Incoming call" : "Webphone"}</span>
        {expanded
          ? <CaretDown className="w-3.5 h-3.5 opacity-90" weight="bold" />
          : <CaretUp className="w-3.5 h-3.5 opacity-90" weight="bold" />}
      </button>
      {expanded && (
        <div className="flex flex-col shadow-sm" data-testid="agent-webphone-panel">
          <div className="px-3 pt-2 pb-1 text-xs text-slate-700 min-h-[2.5rem]">
            <p
              className={`font-medium break-words ${status === "error" ? "text-red-600" : ""}`}
              data-testid="agent-webphone-status"
            >
              {statusLine || (status === "connecting" ? "Registering…" : "Idle")}
            </p>
            <div className="mt-1 h-8 border border-slate-200 rounded-sm px-2 flex items-center gap-1 font-mono text-sm bg-white">
              <span className="flex-1 min-w-0 truncate" data-testid="agent-webphone-digits">
                {digits || <span className="text-muted-foreground">&nbsp;</span>}
              </span>
              {digits ? (
                <button
                  type="button"
                  className="shrink-0 w-6 h-6 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center"
                  onClick={() => setDigits("")}
                  title="Clear"
                  aria-label="Clear digits"
                  data-testid="agent-webphone-clear"
                >
                  <X className="w-3.5 h-3.5" weight="bold" />
                </button>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-3 border-t border-slate-200" data-testid="agent-webphone-pad">
            {KEYS.map((k) => (
              <button
                key={k}
                type="button"
                className="h-10 text-sm text-slate-500 border-b border-r border-slate-200 last:border-r-0 hover:bg-slate-50"
                onClick={() => pressKey(k)}
                data-testid={`agent-webphone-key-${k === "*" ? "star" : k === "#" ? "hash" : k}`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-center gap-5 py-3">
            <button
              type="button"
              className={`relative w-11 h-11 rounded-full text-white flex items-center justify-center shadow-sm disabled:opacity-40 ${
                isRinging
                  ? "bg-emerald-500 hover:bg-emerald-400 scale-110 shadow-emerald-400/50 shadow-md ring-2 ring-emerald-300"
                  : isAnswering
                    ? "bg-emerald-500 opacity-70"
                    : "bg-emerald-500 hover:bg-emerald-600"
              }`}
              onClick={isRinging ? answer : dial}
              disabled={isRinging ? answerLocked : (!digits.trim() || status === "connecting" || answerLocked)}
              title={isAnswering ? "Connecting…" : isRinging ? "Answer" : "Call"}
              aria-label={isRinging ? "Answer incoming call" : undefined}
              data-testid="agent-webphone-call"
            >
              {isRinging && (
                <>
                  <span
                    className="pointer-events-none absolute inset-0 rounded-full bg-emerald-400/70 animate-ping"
                    aria-hidden
                  />
                  <span
                    className="pointer-events-none absolute -inset-2 rounded-full border-2 border-emerald-400/60 animate-ping"
                    style={{ animationDuration: "1.4s" }}
                    aria-hidden
                  />
                </>
              )}
              <Phone className="relative z-10 w-5 h-5" weight="fill" />
            </button>
            <button
              type="button"
              className="w-11 h-11 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm hover:bg-red-600 disabled:opacity-40"
              onClick={requestHangup}
              disabled={!inCall && !isRinging && !isAnswering}
              title={isRinging ? "Decline" : "Hang up"}
              data-testid="agent-webphone-hangup"
            >
              <PhoneDisconnect className="w-5 h-5" weight="fill" />
            </button>
            <button
              type="button"
              className={`w-11 h-11 rounded-full text-white flex items-center justify-center shadow-sm ${
                muted ? "bg-amber-600 hover:bg-amber-700" : "bg-[#1e3a5f] hover:bg-[#16304f]"
              }`}
              onClick={toggleMute}
              title={muted ? "Unmute" : "Mute"}
              data-testid="agent-webphone-mute"
            >
              {muted
                ? <MicrophoneSlash className="w-5 h-5" weight="fill" />
                : <Microphone className="w-5 h-5" weight="fill" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
